import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BIBLE_ICONS = ["BookOpen", "Crown", "ShieldAlert", "AlertTriangle"];

/* Small atoms — keep this editor self-contained */
const Field = ({ label, children, hint }) => (
  <label className="block mb-4">
    <span className="block text-[10px] uppercase tracking-[0.26em] text-[#D4AF37] mb-2">
      {label}
    </span>
    {children}
    {hint && <span className="block text-xs text-[#B6B6BE]/65 mt-2">{hint}</span>}
  </label>
);

const TextInput = ({ value, onChange, placeholder }) => (
  <input
    type="text"
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="search-input w-full px-3 py-3 rounded-xl bg-[#0B0B0F]/90 border border-white/10 text-sm text-[#F5F5F5] focus:outline-none focus:border-[#D4AF37]/55"
  />
);

const TextArea = ({ value, onChange, rows = 4 }) => (
  <textarea
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    className="search-input w-full px-3 py-3 rounded-xl bg-[#0B0B0F]/90 border border-white/10 text-sm text-[#F5F5F5] focus:outline-none focus:border-[#D4AF37]/55 leading-relaxed"
  />
);

const SectionCard = ({ s, idx, total, onChange, onRemove, onMove }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-3 rounded-xl border border-white/8 bg-[#08080C]/70">
      <div className="flex items-center gap-2 p-3">
        <button
          onClick={() => setOpen((o) => !o)}
          className="p-1.5 rounded text-[#B6B6BE] hover:text-[#F5F5F5]"
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <span className="text-sm text-[#F5F5F5] truncate flex-1 font-medium">
          {s.h || "(senza titolo)"}
        </span>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={() => onMove(idx, -1)}
            disabled={idx === 0}
            className="p-1.5 rounded border border-white/10 text-[#B6B6BE] disabled:opacity-30"
          >
            <ArrowUp size={11} />
          </button>
          <button
            onClick={() => onMove(idx, 1)}
            disabled={idx === total - 1}
            className="p-1.5 rounded border border-white/10 text-[#B6B6BE] disabled:opacity-30"
          >
            <ArrowDown size={11} />
          </button>
          <button
            onClick={onRemove}
            className="p-1.5 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-white/[0.04]">
          <Field label="Titolo sezione">
            <TextInput value={s.h} onChange={(x) => onChange({ ...s, h: x })} />
          </Field>
          <Field label="Body (righe vuote = paragrafi separati)">
            <TextArea value={s.body} onChange={(x) => onChange({ ...s, body: x })} rows={6} />
          </Field>
        </div>
      )}
    </div>
  );
};

const ChapterCard = ({ c, idx, total, onChange, onRemove, onMove }) => {
  const [open, setOpen] = useState(false);

  const updateSection = (si, next) =>
    onChange({ ...c, sections: c.sections.map((s, i) => (i === si ? next : s)) });
  const removeSection = (si) =>
    onChange({ ...c, sections: c.sections.filter((_, i) => i !== si) });
  const moveSection = (si, dir) => {
    const j = si + dir;
    if (j < 0 || j >= c.sections.length) return;
    const arr = [...c.sections];
    [arr[si], arr[j]] = [arr[j], arr[si]];
    onChange({ ...c, sections: arr });
  };
  const addSection = () =>
    onChange({ ...c, sections: [...c.sections, { h: "Nuova sezione", body: "Contenuto…" }] });

  return (
    <div className="mb-5 rounded-2xl border border-[#E26B6B]/20 bg-[#0B0B0F]/70 overflow-hidden">
      <div className="flex items-center gap-3 p-4 sm:p-5 bg-gradient-to-r from-[#1A0E12]/70 to-transparent">
        <button
          onClick={() => setOpen((o) => !o)}
          className="p-1.5 rounded text-[#B6B6BE] hover:text-[#F5F5F5]"
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-mono-pl text-[9px] uppercase tracking-[0.22em] text-[#E26B6B]">
            Cap. {String(idx + 1).padStart(2, "0")} · {c.icon}
          </p>
          <p className="font-display text-base sm:text-lg uppercase text-[#F5F5F5] tracking-wide truncate">
            {c.title || "(senza titolo)"}
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={() => onMove(idx, -1)}
            disabled={idx === 0}
            className="p-2 rounded border border-white/10 text-[#B6B6BE] disabled:opacity-30"
          >
            <ArrowUp size={12} />
          </button>
          <button
            onClick={() => onMove(idx, 1)}
            disabled={idx === total - 1}
            className="p-2 rounded border border-white/10 text-[#B6B6BE] disabled:opacity-30"
          >
            <ArrowDown size={12} />
          </button>
          <button
            onClick={onRemove}
            className="p-2 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      {open && (
        <div className="p-5 sm:p-6 border-t border-white/[0.04]">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Chiave (url-safe)">
              <TextInput
                value={c.key}
                onChange={(x) => onChange({ ...c, key: x.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
              />
            </Field>
            <Field label="Icona">
              <select
                value={c.icon || "BookOpen"}
                onChange={(e) => onChange({ ...c, icon: e.target.value })}
                className="search-input w-full px-3 py-3 rounded-xl bg-[#0B0B0F]/90 border border-white/10 text-sm text-[#F5F5F5]"
              >
                {BIBLE_ICONS.map((ic) => (
                  <option key={ic} value={ic}>{ic}</option>
                ))}
              </select>
            </Field>
            <Field label="Titolo">
              <TextInput value={c.title} onChange={(x) => onChange({ ...c, title: x })} />
            </Field>
          </div>
          <Field label="Sottotitolo">
            <TextInput value={c.subtitle} onChange={(x) => onChange({ ...c, subtitle: x })} />
          </Field>
          <div className="mt-4 pt-4 border-t border-white/[0.05]">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#E26B6B]">
                Sezioni ({c.sections.length})
              </p>
              <button
                onClick={addSection}
                className="btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.16em]"
              >
                <Plus size={12} /> Sezione
              </button>
            </div>
            {c.sections.map((s, i) => (
              <SectionCard
                key={i}
                s={s}
                idx={i}
                total={c.sections.length}
                onChange={(next) => updateSection(i, next)}
                onRemove={() => removeSection(i)}
                onMove={moveSection}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const BibleEditor = () => {
  const [v, setV] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/staff/bible`, { withCredentials: true });
        setV(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Impossibile caricare la Bibbia");
      }
    })();
  }, []);

  const update = (next) => {
    setV(next);
    setDirty(true);
  };

  const save = async () => {
    if (!v) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/staff/bible`,
        { value: v },
        { withCredentials: true },
      );
      toast.success("Bibbia aggiornata.", { id: "bible-save-success" });
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore di salvataggio");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [v]);

  if (!v) {
    return <p className="text-sm text-[#B6B6BE]">Caricamento Bibbia…</p>;
  }

  const chapters = Array.isArray(v.chapters) ? v.chapters : [];

  const updateChapter = (i, next) =>
    update({ ...v, chapters: chapters.map((c, j) => (j === i ? next : c)) });
  const removeChapter = (i) =>
    update({ ...v, chapters: chapters.filter((_, j) => j !== i) });
  const moveChapter = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= chapters.length) return;
    const arr = [...chapters];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    update({ ...v, chapters: arr });
  };
  const addChapter = () =>
    update({
      ...v,
      chapters: [
        ...chapters,
        {
          key: `cap-${Date.now()}`,
          icon: "BookOpen",
          title: "Nuovo capitolo",
          subtitle: "Sottotitolo",
          sections: [],
        },
      ],
    });

  return (
    <div data-testid="editor-bible" className="max-w-4xl">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#E26B6B] mb-2">
            Vault Staff · Sezione riservata
          </p>
          <h2 className="font-display text-2xl sm:text-3xl uppercase text-[#F5F5F5] tracking-wide">
            La Bibbia della Regia
          </h2>
          <p className="text-sm text-[#B6B6BE] mt-2 max-w-2xl">
            Documento interno staff. Visibile solo dal vault <code className="text-[#E26B6B]">/staff/bible</code> a chi è loggato.
            Mai esposto via API pubblica.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <a
            href="/staff/bible"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em]"
          >
            <ExternalLink size={12} /> Apri vault
          </a>
          <button
            onClick={save}
            disabled={saving || !dirty}
            data-testid="editor-bible-save"
            className="btn-gold rounded-full px-6 py-2.5 text-xs font-bold uppercase tracking-[0.18em] disabled:opacity-60"
          >
            {saving ? "Salvataggio…" : dirty ? "Salva (⌘S)" : "Salvato"}
          </button>
        </div>
      </div>

      <div className="mb-6 rounded-xl px-4 py-3 border border-[#E26B6B]/30 bg-[#E26B6B]/5 flex items-start gap-2">
        <ShieldAlert size={14} className="text-[#E26B6B] mt-0.5 shrink-0" />
        <p className="text-xs text-[#B6B6BE] leading-relaxed">
          <span className="text-[#E26B6B] font-semibold uppercase tracking-[0.18em] text-[10px]">
            Interno
          </span>{" "}
          — La Bibbia non viene mai inclusa nelle risposte di <code>/api/content</code>. Gli endpoint{" "}
          <code>/api/staff/bible</code> richiedono autenticazione admin. Ogni salvataggio è loggato in audit.
        </p>
      </div>

      {/* META */}
      <div className="mb-8 pb-6 border-b border-white/[0.06]">
        <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#00D4FF] mb-3">
          Intestazione documento
        </p>
        <Field label="Titolo">
          <TextInput
            value={v.meta?.title}
            onChange={(x) => update({ ...v, meta: { ...v.meta, title: x } })}
          />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Sottotitolo / Watermark">
            <TextInput
              value={v.meta?.subtitle}
              onChange={(x) => update({ ...v, meta: { ...v.meta, subtitle: x } })}
            />
          </Field>
          <Field label="Edizione">
            <TextInput
              value={v.meta?.edition}
              onChange={(x) => update({ ...v, meta: { ...v.meta, edition: x } })}
            />
          </Field>
        </div>
        <Field label="Intro (visibile in cima al documento)">
          <TextArea
            value={v.meta?.intro}
            onChange={(x) => update({ ...v, meta: { ...v.meta, intro: x } })}
            rows={3}
          />
        </Field>
      </div>

      {/* CHAPTERS */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#E26B6B]">
            Capitoli del documento ({chapters.length})
          </p>
          <button
            onClick={addChapter}
            data-testid="add-bible-chapter"
            className="btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.16em]"
          >
            <Plus size={12} /> Capitolo
          </button>
        </div>
        {chapters.map((c, i) => (
          <ChapterCard
            key={i}
            c={c}
            idx={i}
            total={chapters.length}
            onChange={(next) => updateChapter(i, next)}
            onRemove={() => removeChapter(i)}
            onMove={moveChapter}
          />
        ))}
      </div>
    </div>
  );
};
